'use client';

import { Button } from '@ahafood/ui/components/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@ahafood/ui/components/dialog';
import { Image } from '@ahafood/ui/components/image';
import { Sheet, SheetContent, SheetTitle } from '@ahafood/ui/components/sheet';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { useState } from 'react';
import { useIsMobile } from '@/lib/is-mobile';
import { useNewAuth } from '@/providers/new-auth-provider';

interface NewLoginDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  location?: string;
  /** Optional ghi đè redirect target sau khi login (default: URL hiện tại). */
  redirectTo?: string;
}

/**
 * Single-button login dialog — bounce ra accounts.ahamove.com.
 * Replace dialog cũ với OTP form / Facebook (sẽ xóa ở PR 3).
 */
export function NewLoginDialog({
  open,
  onOpenChange,
  redirectTo,
}: NewLoginDialogProps) {
  const isMobile = useIsMobile();
  const { login } = useNewAuth();
  const [busy, setBusy] = useState(false);

  const handleLogin = () => {
    if (busy) return;
    setBusy(true);
    login(redirectTo);
  };

  const content = (
    <div className="flex flex-col items-center gap-5 py-6 px-4">
      <Image
        src={`${assetPrefix}/static/images/aha-food-logo.webp`}
        alt="AhaFood"
        width={64}
        height={64}
        className="size-16 object-contain"
      />
      <div className="flex flex-col items-center gap-1 text-center">
        <h2 className="text-lg font-bold text-neutral-90">Đăng nhập AhaFood</h2>
        <p className="text-sm text-neutral-60">
          Đăng nhập bằng tài khoản Ahamove để tiếp tục.
        </p>
      </div>
      <Button
        onClick={handleLogin}
        disabled={busy}
        size="lg"
        className={cn('w-full font-bold')}
      >
        {busy ? 'Đang chuyển hướng...' : 'Đăng nhập với Ahamove'}
      </Button>
      <button
        type="button"
        onClick={() => onOpenChange(false)}
        className="text-sm text-neutral-50 hover:underline"
      >
        Để sau
      </button>
    </div>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="rounded-t-3xl">
          <SheetTitle className="sr-only">Đăng nhập</SheetTitle>
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogTitle className="sr-only">Đăng nhập</DialogTitle>
        {content}
      </DialogContent>
    </Dialog>
  );
}
